<script setup>
/* global axios */
// Transcrição e análise da ligação, abertas em popup a partir da bolha da gravação.
//
// Fica num componente próprio (e não dentro do Text/Index.vue) para o arquivo do
// core levar só duas linhas: import + tag. Quem decide se aparece é este componente.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { useMessageContext } from './provider.js';

const { content, attachments, id: messageId } = useMessageContext();
const { t } = useI18n();
const route = useRoute();

// Só na nota da gravação da ligação: um áudio comum do cliente não tem transcrição
// de ligação nenhuma. O texto é o que o provedor manda junto do anexo.
const ehGravacaoDeLigacao = computed(
  () =>
    /Gravação da chamada/.test(content.value || '') &&
    (attachments.value || []).some(a => a.fileType === 'audio')
);

const dialogRef = ref(null);
const titulo = ref('');
const texto = ref('');
const carregando = ref(false);
const erro = ref('');
const ehAnalise = ref(false);

const base = () =>
  `/api/v1/accounts/${route.params.accountId}/kanban/call_transcripts`;

// A análise vem em markdown do modelo; sem renderizar, o popup mostra "**Resumo**"
// e asteriscos de lista na cara do usuário. A transcrição é texto puro e não passa
// pelo formatador (falas com * ou _ não podem virar itálico por acidente).
// A transcrição vem como "Agente: ...\nCliente: ..." e é desenhada como CONVERSA,
// com o mesmo desenho das bolhas do ticket — ler um bloco corrido de texto não diz
// quem falou o quê.
// Quando existe a transcrição da GRAVAÇÃO, é ela que vale: cobre a ligação inteira,
// inclusive o que foi dito depois de a IA transferir. A transcrição ao vivo da ponte
// para no handoff, porque o call server deixa de mandar áudio para nós.
const temGravacao = ref(false);
const completa = ref('');
const carregandoCompleta = ref(false);
const avisoCompleta = ref('');

const textoExibido = computed(() => completa.value || texto.value);

const falas = computed(() => {
  if (ehAnalise.value || !textoExibido.value) return [];
  return textoExibido.value
    .split('\n')
    .map(linha => {
      const m2 = linha.match(/^(Cliente|Agente|Atendente):\s*(.*)$/s);
      return m2 ? { quem: m2[1] === 'Cliente' ? 'Cliente' : 'Agente', texto: m2[2] } : { quem: null, texto: linha };
    })
    .filter(f => f.texto.trim());
});

const analiseFormatada = computed(() =>
  ehAnalise.value && texto.value
    ? new MessageFormatter(texto.value).formattedMessage
    : ''
);

// Exportar sem passar pelo servidor: o texto já está aqui, e um blob local evita
// mais uma rota e mais um caminho de permissão para manter.
const baixar = (nome, conteudo, tipo) => {
  const url = URL.createObjectURL(new Blob([conteudo], { type: `${tipo};charset=utf-8` }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  a.click();
  URL.revokeObjectURL(url);
};

const exportarTxt = () =>
  baixar('transcricao-da-ligacao.txt', textoExibido.value, 'text/plain');

// BOM no começo: sem ele o Excel abre os acentos quebrados, que é onde essa
// planilha quase sempre termina.
const exportarCsv = () => {
  const escapar = v => `"${String(v).replace(/"/g, '""')}"`;
  const linhas = [['quem', 'fala'].map(escapar).join(',')].concat(
    falas.value.map(f => [f.quem || '', f.texto].map(escapar).join(','))
  );
  baixar('transcricao-da-ligacao.csv', `\ufeff${linhas.join('\n')}`, 'text/csv');
};

// Transcrever a gravação custa uma chamada de IA e alguns segundos, então é o
// atendente que pede — e o resultado fica guardado para as próximas aberturas.
const transcreverGravacao = async () => {
  carregandoCompleta.value = true;
  avisoCompleta.value = '';
  try {
    const { data } = await axios.post(`${base()}/full`, {
      message_id: messageId.value,
    });
    completa.value = data?.data?.full_transcript || '';
  } catch (e) {
    // Não joga fora o trecho da IA que já está na tela: se a transcrição da gravação
    // falhar (cota do provedor, por exemplo), o aviso explica o que faltou.
    avisoCompleta.value =
      e?.response?.data?.error || t('CONVERSATION.CALL_TRANSCRIPT.FAILED');
    if (!textoExibido.value) erro.value = avisoCompleta.value;
  } finally {
    carregandoCompleta.value = false;
  }
};

const abrir = async modo => {
  const analise = modo === 'analise';
  ehAnalise.value = analise;
  titulo.value = t(
    analise
      ? 'CONVERSATION.CALL_TRANSCRIPT.ANALYSIS_TITLE'
      : 'CONVERSATION.CALL_TRANSCRIPT.TITLE'
  );
  texto.value = '';
  erro.value = '';
  carregando.value = true;
  dialogRef.value?.open();
  try {
    const { data } = analise
      ? await axios.post(`${base()}/analysis`, { message_id: messageId.value })
      : await axios.get(base(), { params: { message_id: messageId.value } });
    const payload = data?.data || {};
    texto.value = (analise ? payload.analysis : payload.transcript) || '';
    completa.value = analise ? '' : payload.full_transcript || '';
    temGravacao.value = !analise && !!payload.has_recording;
    if (!textoExibido.value && !temGravacao.value) {
      erro.value = t('CONVERSATION.CALL_TRANSCRIPT.EMPTY');
    }
    // A ligação inteira (com o trecho depois da transferência) só existe na gravação.
    // Buscamos sozinho ao abrir; o resultado fica guardado, então isso acontece uma
    // vez por ligação e não a cada abertura.
    if (temGravacao.value && !completa.value) transcreverGravacao();
  } catch (e) {
    erro.value =
      e?.response?.data?.error || t('CONVERSATION.CALL_TRANSCRIPT.FAILED');
  } finally {
    carregando.value = false;
  }
};
</script>

<template>
  <div v-if="ehGravacaoDeLigacao" class="flex flex-wrap gap-2">
    <NextButton
      sm
      faded
      slate
      icon="i-ph-file-text"
      :label="$t('CONVERSATION.CALL_TRANSCRIPT.BUTTON')"
      @click="abrir('transcricao')"
    />
    <NextButton
      sm
      faded
      slate
      icon="i-ph-sparkle"
      :label="$t('CONVERSATION.CALL_TRANSCRIPT.ANALYSIS_BUTTON')"
      @click="abrir('analise')"
    />

    <Dialog
      ref="dialogRef"
      type="edit"
      :title="titulo"
      :show-confirm-button="false"
      :show-cancel-button="false"
    >
      <button
        class="absolute top-3 ltr:right-3 rtl:left-3 grid place-items-center size-7 rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
        type="button"
        :aria-label="$t('CONVERSATION.CALL_TRANSCRIPT.CLOSE')"
        @click="dialogRef?.close()"
      >
        <span class="i-lucide-x size-4" />
      </button>
      <div class="overflow-y-auto max-h-[60vh] min-h-[6rem]">
        <p v-if="carregando" class="py-6 text-sm text-center text-n-slate-11">
          {{ $t('CONVERSATION.CALL_TRANSCRIPT.LOADING') }}
        </p>
        <p v-else-if="erro" class="py-6 text-sm text-center text-n-ruby-11">
          {{ erro }}
        </p>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div
          v-else-if="ehAnalise"
          class="text-sm leading-relaxed prose-sm text-n-slate-12 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_p]:mb-2"
          v-html="analiseFormatada"
        />
        <div v-else class="flex flex-col gap-2 py-1">
          <div
            v-for="(fala, i) in falas"
            :key="i"
            class="flex"
            :class="fala.quem === 'Agente' ? 'justify-end' : 'justify-start'"
          >
            <div class="flex flex-col gap-0.5 max-w-[85%]">
              <span
                v-if="fala.quem"
                class="text-xs text-n-slate-11"
                :class="fala.quem === 'Agente' ? 'text-right' : ''"
              >
                {{
                  fala.quem === 'Agente'
                    ? $t('CONVERSATION.CALL_TRANSCRIPT.SPEAKER_AGENT')
                    : $t('CONVERSATION.CALL_TRANSCRIPT.SPEAKER_CUSTOMER')
                }}
              </span>
              <!-- mesmos tokens das bolhas do ticket: bot à direita, cliente à esquerda -->
              <div
                class="px-3 py-2 text-sm leading-relaxed rounded-xl"
                :class="
                  fala.quem === 'Agente'
                    ? 'bg-n-solid-iris text-n-slate-12 rounded-br-sm'
                    : 'bg-n-slate-4 text-n-slate-12 rounded-bl-sm'
                "
              >
                {{ fala.texto }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="!carregando && !ehAnalise && (carregandoCompleta || avisoCompleta)"
        class="flex items-start gap-2 px-3 py-2 mt-2 text-xs rounded-lg bg-n-alpha-1 text-n-slate-11"
      >
        <span
          class="shrink-0 size-4"
          :class="carregandoCompleta ? 'i-ph-waveform' : 'i-ph-warning'"
        />
        <span class="flex-1">
          {{
            carregandoCompleta
              ? $t('CONVERSATION.CALL_TRANSCRIPT.FULL_LOADING')
              : avisoCompleta
          }}
        </span>
      </div>
      <div
        v-if="!carregando && !erro && !ehAnalise && falas.length"
        class="flex gap-2 pt-3 mt-1 border-t border-n-strong"
      >
        <NextButton
          sm
          faded
          slate
          icon="i-ph-download-simple"
          :label="$t('CONVERSATION.CALL_TRANSCRIPT.EXPORT_TXT')"
          @click="exportarTxt"
        />
        <NextButton
          sm
          faded
          slate
          icon="i-ph-table"
          :label="$t('CONVERSATION.CALL_TRANSCRIPT.EXPORT_CSV')"
          @click="exportarCsv"
        />
      </div>
    </Dialog>
  </div>
</template>
